import { formatEventDate, eventMonogram } from "../utils/formatEventDate"
import MasonryCollection from "./components/MasonryCollection"
import { GalleryDesignProps } from "./types"
import PhotographerBar from "./components/PhotographerBar"
import CoverImage from "./components/CoverImage"

/**
 * 04 · Soft & Romantic
 * Light + dark. Warm palette, EB Garamond serif, an arched cover, a centered
 * title with a line-flanked date, and a 2-column (1 on mobile) collection.
 */
const SoftRomanticGallery = ({
  data,
  folderBar,
  contentMode,
  aiContent,
  emptyContent,
  photos,
  onCoverLoad,
}: GalleryDesignProps) => {
  const name = data?.name || ""
  const dateLabel = formatEventDate(data?.startDate, data?.endDate, data?.date)
  const cover = data?.coverPhotoLink
  const vendor = data?.vendors?.[0]
  // "Amelia & Theo" -> "a · t"
  const monogram = eventMonogram(name).toLowerCase().replace(/&/g, "·")

  return (
    <div className="relative w-full bg-[#faf9f8] text-[#2e2b29] dark:bg-[#141210] dark:text-[#efe9e4] font-ebgaramond">
      {/* Photographer bar — logo + name top-left, icons top-right of screen */}
      <PhotographerBar vendor={vendor} tone="light" />

      {/* Arched cover */}
      <div className="flex justify-center px-6 md:px-10">
        <div className="mt-16 md:mt-32 w-full max-w-[480px] aspect-[640/760] rounded-t-full overflow-hidden bg-[#ecebe9] dark:bg-[#221d19]">
          {cover && (
            <CoverImage
              src={cover}
              alt={name}
              className="w-full h-full object-cover"
              onLoad={onCoverLoad}
            />
          )}
        </div>
      </div>

      {/* Title */}
      <div className="text-center px-6 pt-12 md:pt-14 pb-3 md:pb-4">
        <h1 className="font-light italic leading-[1.05] text-[36px] md:text-[60px] lg:text-[70px] break-words">
          {name}
        </h1>
      </div>

      {/* Date flanked by lines + photographer credit */}
      <div className="pb-10 md:pb-14">
        {dateLabel && (
          <div className="flex items-center justify-center gap-3 md:gap-4">
            <span className="h-px w-8 md:w-12 bg-[#d8d3ce] dark:bg-white/30" />
            <span className="text-[11px] md:text-[13px] tracking-[0.26em] md:tracking-[0.34em] uppercase text-[#8f8881] dark:text-[#a59e96]">
              {dateLabel}
            </span>
            <span className="h-px w-8 md:w-12 bg-[#d8d3ce] dark:bg-white/30" />
          </div>
        )}
      </div>

      {/* Folder bar + collection share one tall parent so the sticky folder
          bar stays pinned while scrolling the photos. */}
      <div className="flex flex-col items-center w-full">
        {folderBar}

        <div className="w-full px-6 md:px-16 lg:px-[110px] pt-2 pb-12 min-h-[40vh]">
          {contentMode === "ai" ? (
            aiContent
          ) : contentMode === "photos" ? (
            <MasonryCollection
              photos={photos}
              columnsClass="columns-2 md:columns-3"
            />
          ) : (
            emptyContent
          )}
        </div>
      </div>
    </div>
  )
}

export default SoftRomanticGallery
